<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="sm:flex sm:items-center mb-8">
      <div class="sm:flex-auto">
        <h1 class="text-base font-semibold leading-6 text-gray-900">My Job Posts</h1>
        <p class="mt-2 text-sm text-gray-700">
          Manage all the job opportunities you've shared with the community.
        </p>
      </div>
      <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
        <a
          href="/post-job"
          class="bg-red-900 text-white px-4 py-2 rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Post New Job
        </a>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="mb-6">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label for="search" class="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              v-model="filters.search"
              type="text"
              id="search"
              placeholder="Job title, company, or description"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label for="status" class="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              v-model="filters.status"
              id="status"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          <div>
            <label for="dateRange" class="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              v-model="filters.dateRange"
              id="dateRange"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>
          </div>
          <div class="flex items-end">
            <button
              @click="applyFilters"
              class="w-full bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Job Posts Table -->
    <div v-if="isLoading" class="text-center py-8">
      <div class="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm text-white bg-red-900 rounded-md">
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading...
      </div>
    </div>

    <div v-else-if="errorMessage" class="text-center py-8">
      <div class="text-red-600">{{ errorMessage }}</div>
      <button
        @click="fetchJobPosts"
        class="mt-4 bg-red-900 text-white px-4 py-2 rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
      >
        Try Again
      </button>
    </div>

    <div v-else-if="filteredJobPosts.length === 0" class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.815-8.964-2.489M21 13.255A23.931 23.931 0 0012 15c3.183 0 6.22-.815 8.964-2.489M21 13.255A23.931 23.931 0 0112 15c3.183 0-6.22-.815-8.964-2.489" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No job posts found</h3>
      <p class="mt-1 text-sm text-gray-500">Try adjusting your filters or post your first job opportunity!</p>
      <div class="mt-6">
        <a
          href="/post-job"
          class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-900 hover:bg-red-800"
        >
          Post New Job
        </a>
      </div>
    </div>

    <div v-else class="-mx-4 mt-8 sm:-mx-0">
      <table class="min-w-full divide-y divide-gray-300">
        <thead>
          <tr>
            <th
              scope="col"
              class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
            >
              Job Details
            </th>
            <th
              scope="col"
              class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            >
              Posted Date
            </th>
            <th
              scope="col"
              class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Status
            </th>
            <th
              scope="col"
              class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Views
            </th>
            <th
              scope="col"
              class="relative px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <tr
            v-for="job in filteredJobPosts"
            :key="job.id"
            class="hover:bg-gray-50"
          >
            <td class="w-full max-w-0 py-4 pl-4 pr-3 text-sm sm:w-auto sm:max-w-none sm:pl-0">
              <div class="flex items-center">
                <div class="flex-shrink-0 h-10 w-10">
                  <div class="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.815-8.964-2.489M21 13.255A23.931 23.931 0 0012 15c3.183 0 6.22-.815 8.964-2.489M21 13.255A23.931 23.931 0 0112 15c3.183 0-6.22-.815-8.964-2.489" />
                    </svg>
                  </div>
                </div>
                <div class="ml-4">
                  <div class="font-medium text-gray-900">{{ job.title }}</div>
                  <div class="text-gray-500">{{ job.company }}</div>
                  <div class="text-gray-400 text-sm mt-1">{{ job.description.substring(0, 80) }}...</div>
                </div>
              </div>
            </td>
            <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
              {{ formatDate(job.postedDate) }}
            </td>
            <td class="px-3 py-4 text-sm text-gray-500">
              <span
                :class="getStatusClass(job.status)"
                class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              >
                {{ job.status }}
              </span>
            </td>
            <td class="px-3 py-4 text-sm text-gray-500">
              {{ job.views }}
            </td>
            <td class="relative px-3 py-4 text-sm text-gray-500">
              <div class="flex items-center space-x-2">
                <button
                  @click="viewJobPost(job.id)"
                  class="text-blue-600 hover:text-blue-800 font-medium"
                >
                  View
                </button>
                <button
                  @click="editJobPost(job.id)"
                  class="text-green-600 hover:text-green-800 font-medium"
                >
                  Edit
                </button>
                <button
                  @click="deleteJobPost(job.id)"
                  class="text-red-600 hover:text-red-800 font-medium"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mt-8 flex items-center justify-between">
      <div class="flex items-center text-sm text-gray-700">
        <span>
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems }} results
        </span>
      </div>
      <div class="flex items-center space-x-2">
        <button
          @click="previousPage"
          :disabled="currentPage === 1"
          class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span class="px-3 py-2 text-sm font-medium text-gray-700">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

// Data
const isLoading = ref(true)
const errorMessage = ref('')
const jobPosts = ref([])
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)

// Filters
const filters = ref({
  search: '',
  status: '',
  dateRange: ''
})

// Sample data (replace with API calls)
const sampleJobPosts = [
  {
    id: 1,
    title: 'Senior Software Engineer',
    company: 'TechCorp International',
    description: 'Looking for a senior developer to join our Berlin team. Remote work possible, competitive salary, great benefits.',
    postedDate: '2024-01-15',
    status: 'active',
    views: 45
  },
  {
    id: 2,
    title: 'Marketing Manager',
    company: 'Global Marketing Solutions',
    description: 'Join our Amsterdam office as Marketing Manager. Lead international campaigns and manage a team of 5.',
    postedDate: '2024-01-14',
    status: 'active',
    views: 32
  },
  {
    id: 3,
    title: 'UX Designer',
    company: 'Creative Studios',
    description: 'Remote UX Designer position for international projects. Flexible hours and competitive hourly rate.',
    postedDate: '2024-01-13',
    status: 'active',
    views: 28
  },
  {
    id: 4,
    title: 'Product Manager',
    company: 'Innovation Labs',
    description: 'Lead product development for our international markets. Experience with agile methodologies required.',
    postedDate: '2024-01-10',
    status: 'expired',
    views: 67
  },
  {
    id: 5,
    title: 'Data Scientist',
    company: 'Analytics Corp',
    description: 'Join our data team to build machine learning models for customer insights and business optimization.',
    postedDate: '2024-01-08',
    status: 'draft',
    views: 0
  }
]

// Computed
const filteredJobPosts = computed(() => {
  let filtered = [...jobPosts.value]

  if (filters.value.search) {
    const searchTerm = filters.value.search.toLowerCase()
    filtered = filtered.filter(job =>
      job.title.toLowerCase().includes(searchTerm) ||
      job.company.toLowerCase().includes(searchTerm) ||
      job.description.toLowerCase().includes(searchTerm)
    )
  }

  if (filters.value.status) {
    filtered = filtered.filter(job => job.status === filters.value.status)
  }

  if (filters.value.dateRange) {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (filters.value.dateRange) {
      case 'today':
        filtered = filtered.filter(job => {
          const jobDate = new Date(job.postedDate)
          return jobDate >= today
        })
        break
      case 'week': {
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
        filtered = filtered.filter(job => {
          const jobDate = new Date(job.postedDate)
          return jobDate >= weekAgo
        })
        break
      }
      case 'month': {
        const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
        filtered = filtered.filter(job => {
          const jobDate = new Date(job.postedDate)
          return jobDate >= monthAgo
        })
        break
      }
      case 'quarter': {
        const quarterAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate())
        filtered = filtered.filter(job => {
          const jobDate = new Date(job.postedDate)
          return jobDate >= quarterAgo
        })
        break
      }
    }
  }

  return filtered
})

const totalPages = computed(() => Math.ceil(filteredJobPosts.value.length / itemsPerPage.value))

// Methods
const fetchJobPosts = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // In a real app, this would make an API call
    await new Promise(resolve => setTimeout(resolve, 1000))

    jobPosts.value = sampleJobPosts
    totalItems.value = sampleJobPosts.length
  } catch (error) {
    errorMessage.value = 'Failed to fetch job posts. Please try again.'
    console.error('Error fetching job posts:', error)
  } finally {
    isLoading.value = false
  }
}

const applyFilters = () => {
  currentPage.value = 1
  // In a real app, this would trigger an API call with the new filters
}

const viewJobPost = (jobId) => {
  router.push(`/admin/job-posts/${jobId}`)
}

const editJobPost = (jobId) => {
  router.push(`/admin/job-posts/${jobId}/edit`)
}

const deleteJobPost = (jobId) => {
  if (confirm('Are you sure you want to delete this job post? This action cannot be undone.')) {
    // In a real app, this would make an API call to delete the job post
    jobPosts.value = jobPosts.value.filter(job => job.id !== jobId)
    totalItems.value--
  }
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const getStatusClass = (status) => {
  const statusClasses = {
    active: 'bg-green-100 text-green-800',
    expired: 'bg-yellow-100 text-yellow-800',
    draft: 'bg-gray-100 text-gray-800'
  }
  return statusClasses[status] || 'bg-gray-100 text-gray-800'
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Lifecycle
onMounted(() => {
  fetchJobPosts()
})
</script>
